import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const MAX_COMPARE = 3;
const STORAGE_KEY = 'toolnova_compare_v1';

interface CompareContextType {
  compareSlugs: string[];
  toggleTool: (slug: string) => void;
  removeTool: (slug: string) => void;
  clearAll: () => void;
  isInCompare: (slug: string) => boolean;
  isFull: boolean;
  count: number;
}

const CompareContext = createContext<CompareContextType | null>(null);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareSlugs, setCompareSlugs] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const toggleTool = useCallback((slug: string) => {
    setCompareSlugs(prev => {
      const next = prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : prev.length < MAX_COMPARE ? [...prev, slug] : prev;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeTool = useCallback((slug: string) => {
    setCompareSlugs(prev => {
      const next = prev.filter(s => s !== slug);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCompareSlugs([]);
  }, []);

  const isInCompare = useCallback(
    (slug: string) => compareSlugs.includes(slug),
    [compareSlugs]
  );

  return (
    <CompareContext.Provider
      value={{
        compareSlugs,
        toggleTool,
        removeTool,
        clearAll,
        isInCompare,
        isFull: compareSlugs.length >= MAX_COMPARE,
        count: compareSlugs.length,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
}
